/**
 * CurrencyTextFilter — per-column currency-masked filter input.
 *
 * Round 2 of the per-filter design-system migration. Routes the
 * trigger through `@shared/ui/Input` primitive (Tier 0 form-field
 * spec). `react-currency-format` handles the `$ ` prefix, comma
 * thousand-separator and 2-decimal mask; `<Input>` carries the
 * visual.
 *
 * Behaviour preserved 1:1 vs. legacy:
 *   - Uncontrolled `<input>` mirrored to `filterValue` via the
 *     parent. `useEffect` syncs back imperatively when `filterValue`
 *     changes externally (e.g. clear-all).
 *   - `onChange` and `onBlur` both commit the unmasked value through
 *     `onChange(dataKey, value, true)` (instant filter).
 *   - Enter triggers the same handler.
 *
 * No clear button (parity with legacy). Adding one through
 * `customInput` would require either an unstable inline component
 * (remounts the input on each parent render, losing focus) or a
 * breaking change to react-currency-format prop forwarding. Worth a
 * separate commit if we ever want it.
 */
import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import CurrencyFormat from 'react-currency-format';
import { useSelector } from 'react-redux';
import Input from '@shared/ui/Input';

const CurrencyTextFilter = ({ filterValue = '', dataKey, onChange }) => {
  const textInput = useRef(null);

  useEffect(() => {
    if (!textInput.current) return;
    if (filterValue === '') {
      textInput.current.value = '';
    } else if (textInput.current.value === '' && filterValue !== null) {
      textInput.current.value = filterValue;
    }
  }, [filterValue]);

  const handleInputChange = e => {
    if (onChange) {
      const value = e.target.value.replace('$ ', '');
      onChange(dataKey, value, true);
    }
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      handleInputChange(e);
    }
  };

  return (
    <CurrencyFormat
      inputRef={textInput}
      prefix={'$ '}
      value={filterValue}
      customInput={_CustomInput}
      // Custom prop forwarded through `react-currency-format`'s
      // otherProps (anything not in the lib's own propTypes is
      // spread into the customInput's props). Lets _CustomInput
      // derive its `highlighted` flag from the parent's raw
      // filterValue, which is reliable on first render — the
      // lib's masked `value` lags one tick behind, so reading
      // `props.value` inside the customInput would miss the
      // initial highlighted state when the table mounts with a
      // pre-set filter.
      hasFilterValue={!!filterValue}
      allowNegative={false}
      fixedDecimalScale={true}
      onKeyDown={handleKeyDown}
      decimalScale={2}
      onBlur={handleInputChange}
      onChange={handleInputChange}
      thousandSeparator={','}
    />
  );
};

// Receives masked `value` + standard input handlers from
// `<CurrencyFormat>` plus `inputRef` for the underlying <input>
// element. We translate `inputRef` into the forwardRef'd `ref` that
// `<Input>` exposes, then layer on the design-system look.
const _CustomInput = ({ inputRef, hasFilterValue, ...props }) => {
  const { tables } = useSelector(state => state.i18n);
  return (
    <Input
      {...props}
      ref={inputRef}
      size="sm"
      fullWidth
      placeholder={tables.filter}
      autoComplete="off"
      type="search"
      highlighted={!!hasFilterValue}
    />
  );
};

CurrencyTextFilter.propTypes = {
  filterValue: PropTypes.string,
  dataKey: PropTypes.string.isRequired,
  columnData: PropTypes.shape({
    isFilterable: PropTypes.bool,
  }).isRequired,
  onChange: PropTypes.func,
};

export default CurrencyTextFilter;
